import { InstallationProvider } from '../InstallationProvider';
import { LocationOptionsData, SubmissionDataTabsDefinition } from 'types/types.type';
import { PropertyColumn, SubmissionDetails } from 'types/submission.type';
import { DataGridColumn } from 'components/Sov/components/SovEditGrid/components/DataGrid';
import { getSOVEditColumns } from './SOVEditGridDefs';
import { getSovColumnDefs } from './SOVGridDefs';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { ComponentType } from 'react';
import { RouteComponentProps } from 'react-router';
import { WithStyles } from '@mui/styles';
import PPAddress from 'views/SubmissionDetails/components/Address/PPAddress';
import { SubmissionURL } from 'services/config';
import { PPValuesSection } from 'components/LrProducerDashboard/components/LocationMap/components/LocationValuesSection';
import { MapLocation } from 'types/location.type';
import Location from 'application/Models/Location';
import { ReportingTab } from 'types/reporting.type';
import { getReportingTabs } from 'helpers/permissions/PPReporting';
import { NavLink } from 'types/nav.types';
import { getNavLinks, getConfigNavLinks } from 'helpers/permissions';
import { PPNavLinksConfig } from 'helpers/permissions/PPNavLinks';
import { PPNavActionsConfig } from 'helpers/permissions/PPActions';
import { Theme } from '@mui/material';
import { PPBaseColumnDefs } from 'views/Submissions/components/PPSubmissionColumnDefs';
import { getPPSubmissionTabDefinition } from 'views/Submissions/components/PPSubmissionListDefs';
import PPUWGuidelinesInfo from 'components/SideNav/components/PPUWGuidelinesInfo';
import React from 'react';
import AdjustmentReview from 'views/SubmissionDetails/components/UnderwriterReview/components/CommercialProperty/AdjustmentReview';
import { AdjustmentReviewProps } from 'views/SubmissionDetails/components/UnderwriterReview';
import { PRODUCT_LINE_TYPES } from 'constants/index';
import AdjustmentGLReview from 'views/SubmissionDetails/components/UnderwriterReview/components/GeneralLiability/AdjustmentGLReview';
import { PPLrProducerDashboard, ProducerDashboardProps } from 'components/LrProducerDashboard';
import { Organization } from 'types/organization.type';
import { EXTENSION_CONFIRM_DIALOG_COPY } from 'constants/copy';
import { extendPolicySchema } from 'views/SubmissionDetails/components/EndorsementDialogs/components/ValidationSchema';

export class PPDefaultInstallationProvider implements InstallationProvider {
    getProducerDashboardComponenet(
        producer?: Organization,
        productLineCode?: string,
        submissionId?: string
    ): ComponentType<ProducerDashboardProps> {
        return PPLrProducerDashboard;
    }
    getDoesAllowTRIADecline(): boolean {
        return false;
    }

    getSovEditGridDef(
        locationOptions: LocationOptionsData,
        disabledColumns?: PropertyColumn[]
    ): Record<PropertyColumn, DataGridColumn> {
        return getSOVEditColumns(locationOptions, disabledColumns);
    }

    getSovGridDef(
        submission: SubmissionDetails,
        isAgent: boolean,
        locationOptions: LocationOptionsData
    ): (ColDef | ColGroupDef)[] {
        return getSovColumnDefs(submission, isAgent, locationOptions);
    }

    canEditDirectory(): boolean {
        return true;
    }

    getLocationReviewComponent(): ComponentType<
        RouteComponentProps<{ id: string; locationId: string }> & WithStyles<any>
    > {
        return PPAddress;
    }

    getLocationAccumulationsSummaryUrl(submissionId: string, locationIds: number[]): string {
        return `${SubmissionURL}/submission/${submissionId}/accumulation-summary?locationIds=${locationIds.join(',')}`;
    }

    getUnderwritingTrendUrl(submissionId: string, locationId: number): string {
        return `${SubmissionURL}/submission/${submissionId}/zone-underwriting-trend?locationId=${locationId}`;
    }

    getReportingTabs(group: string): ReportingTab[] {
        return getReportingTabs(group);
    }

    getNavLinks(group: string): NavLink[] {
        return getNavLinks(PPNavLinksConfig, group);
    }

    getConfigNavLinks(group: string): NavLink[] {
        return getConfigNavLinks(PPNavLinksConfig, group);
    }

    getActionsConfig() {
        return PPNavActionsConfig;
    }

    getSubmissionListColumnDefs(theme: Theme): any[] {
        return PPBaseColumnDefs(theme);
    }

    getSubmissionTabDefinition(userGroup: string): SubmissionDataTabsDefinition {
        return getPPSubmissionTabDefinition(userGroup);
    }

    getUwGuidelinesMenuItems() {
        return <PPUWGuidelinesInfo />;
    }

    getLocationValuesComponent(): ComponentType<{ locationData: MapLocation | Location }> {
        return PPValuesSection;
    }

    getIssuanceChecklist(): string[] {
        return ['Confirm Signed TRIA form is present.', 'Confirm Signed Loss Cert is present.'];
    }

    getAdjustmentReviewScreen(props: AdjustmentReviewProps, productType?: string) {
        switch (productType) {
            case PRODUCT_LINE_TYPES.GENERAL_LIABILITY:
                return <AdjustmentGLReview {...props} />;
            default:
                return <AdjustmentReview {...props} />;
        }
    }

    getPrimaryProductLineCode(): string {
        return 'COMMERCIAL_PROPERTY';
    }

    getExtensionDialogCopy(namedInsured?: string, expirationDate?: string): string {
        return EXTENSION_CONFIRM_DIALOG_COPY(namedInsured, expirationDate);
    }

    getExtensionValidationSchema(expirationDate: string) {
        return extendPolicySchema;
    }

    usesDateExtension(): boolean {
        return false;
    }

    showCountOnMapCluster(productLineCode: string): boolean {
        return productLineCode === 'GENERAL_LIABILITY';
    }
}
